import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSync } from "@/context/SyncContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link2, AlertTriangle, X, Loader2 } from "lucide-react";

// Restores the explicit Same-View pairing prompt for the current account after a reload or when
// returning to the property. Pairing is NEVER called automatically — the user must confirm. Records
// survive reload (owner-scoped). Before offering pairing we verify (read-only) that BOTH the
// baseline and the uploaded photo still exist and are owned; if either is unavailable we show an
// honest recovery state and never re-upload or silently discard the pending record.
export function PairingPendingPrompt({ propertyId }) {
  const { pairingForProperty, confirmPairing, dismissPairing, deletePairing } = useSync();
  const [busy, setBusy] = useState(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [checks, setChecks] = useState({}); // rec.id -> "checking" | "ok" | "unavailable"
  const all = pairingForProperty ? pairingForProperty(propertyId) : [];

  const active = all.filter((p) => !p.dismissed || showDismissed);
  const dismissedCount = all.filter((p) => p.dismissed).length;

  // Verify existence/ownership of each active record's photos before offering "Pair now".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const rec of active) {
        if (checks[rec.id]) continue;
        setChecks((c) => ({ ...c, [rec.id]: "checking" }));
        try {
          const { data } = await api.get(`/properties/${rec.property_id}/same-view/pair-check`, {
            params: { baseline_photo_id: rec.baseline_photo_id, move_out_photo_id: rec.move_out_photo_id },
          });
          if (!cancelled) setChecks((c) => ({ ...c, [rec.id]: data?.ok ? "ok" : "unavailable" }));
        } catch {
          // Network/transient error — don't condemn the record; allow a manual confirm attempt.
          if (!cancelled) setChecks((c) => ({ ...c, [rec.id]: "ok" }));
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.map((r) => r.id).join(","), propertyId]);

  if (!all.length) return null;

  const doConfirm = async (rec) => {
    setBusy(rec.id);
    const res = await confirmPairing(rec);
    setBusy(null);
    if (res.ok) toast.success("Photo paired to the move-in baseline.");
    else if (res.needsAttention) toast.error("That baseline is no longer available — this pairing needs attention.");
    else toast.error("Pairing didn't complete. Your photo is safe; try again.");
  };

  if (!active.length && dismissedCount > 0) {
    return (
      <button data-testid="pairing-resume-btn" onClick={() => setShowDismissed(true)}
        className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-[#EEF2FF] text-[#3730A3] text-xs font-medium px-3 py-1.5">
        <Link2 size={13} /> Resume pairing ({dismissedCount})
      </button>
    );
  }

  return (
    <div data-testid="pairing-pending-prompt" className="mb-3 space-y-2">
      {active.map((rec) => {
        const state = checks[rec.id];
        const unavailable = state === "unavailable";
        const attention = rec.status === "needs_attention" || unavailable;
        return (
          <div key={rec.id} data-testid={`pairing-pending-${rec.id}`}
            className={`rounded-xl border p-3 ${attention ? "border-[#FCA5A5] bg-[#FEF2F2]" : "border-[#C7D2FE] bg-[#EEF2FF]"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-medium flex items-center gap-1.5 text-[#3730A3]">
                {attention ? <AlertTriangle size={15} className="text-[#B91C1C]" /> : <Link2 size={15} />}
                {attention ? "Same-View pairing needs attention" : "Confirm your Same-View pairing"}
              </div>
              <button data-testid={`pairing-close-${rec.id}`} onClick={() => dismissPairing(rec.id)} className="text-[#6B7280]"><X size={16} /></button>
            </div>
            <p className="text-xs text-[#4B5563] mt-1">
              {unavailable
                ? "The move-in baseline or the uploaded photo is no longer available for this account. Any photo already uploaded is safe on the server. You can dismiss this task."
                : attention
                ? "The move-in baseline is no longer available. Your uploaded photo is safe. You can dismiss this task."
                : "Your move-out photo uploaded. Confirm to match it to the move-in baseline."}
            </p>
            <div className="flex gap-2 mt-2 items-center">
              {state === "checking" && (
                <span data-testid={`pairing-checking-${rec.id}`} className="inline-flex items-center gap-1 text-xs text-[#6B7280]"><Loader2 size={13} className="animate-spin" /> Checking…</span>
              )}
              {!attention && state === "ok" && (
                <Button data-testid={`pairing-confirm-${rec.id}`} disabled={busy === rec.id} onClick={() => doConfirm(rec)} size="sm" className="rounded-lg text-xs bg-[#4338CA] hover:bg-[#3730A3]">Pair now</Button>
              )}
              {(attention || state !== "checking") && (
                <Button data-testid={`pairing-dismiss-${rec.id}`}
                  onClick={() => (attention ? deletePairing(rec.id) : dismissPairing(rec.id))}
                  size="sm" variant="outline" className="rounded-lg text-xs">
                  {attention ? "Dismiss" : "Later"}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
